// DirectoryNav, slim ivory utility layer that sits ABOVE the Edit Masthead.
//
// Design intent: this is the "private members navigation", quiet, controlled,
// almost invisible until needed. Not a public menu shouting at the reader.
// The Edit Masthead beneath it owns the editorial identity.
//
//   ┌─ DirectoryNav ─ ivory · 8px caps · 0.30em ─ subtle ink, gold-on-hover ┐
//   │  LWD                      Destinations · Venues · Vendors · …        │
//   └───────────────────────────────────────────────────────────────────────┘
//
// LWD anchors the platform on the left (the global identity); the directory
// destinations group on the right. Everything reads as "utility", letting
// THE EDIT below be the dominant visual voice.

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { GOLD, FU } from './editorialTheme.js';
import { navigateTo } from '../../utils/navigate';
import { imgUrl } from '../../utils/imgUrl';

// Black lockup (this bar is ivory/light) — same approved logo asset as the
// site header (project_lwd_masthead_rebrand, 2026-09-12). Was a plain "H"
// text link; replaced per Tai's direct request to put the real logo far left.
const LOGO_BLACK = imgUrl('https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/brand-assets/logo/luxury-wedding-directory-serif-black.png', { width: 240, height: 64 });

// Ivory surface, slightly warmer than pure white so it doesn't fight the
// Edit's cream typography. Keeps a luxe feel on a dark hero photograph.
const IVORY_DIM = 'rgba(247,243,236,0.96)';
// Lower-contrast ink so the bar reads as utility, not as a primary nav.
// Resting state ~50% so it doesn't compete with THE EDIT below; hover lifts
// to gold. The "almost invisible until needed" feel.
const INK_DIM   = 'rgba(22,20,15,0.50)';
const INK_FULL  = '#16140f';
const HAIRLINE  = 'rgba(201,168,110,0.18)';

// Routes are absolute hrefs because:
//   1. The Edit pages don't receive setPage() handlers for directory
//      destinations, the site routes there cold via URL.
//   2. A full navigation gives the directory pages their own clean mount,
//      no leftover Edit theme state on <html>.
// Mirrors the main directory top nav so The Edit's masthead reads as
// part of the same site, not a parallel navigation. Order matches the
// main nav (Venues → Vendors → Destinations → The Edit → Publications)
// minus the Apply for Inclusion CTA (rendered separately in the masthead
// lockup on the right). The H home ornament (see HomeNav.jsx) is rendered
// on the left in its place, see below.
const ITEMS = [
  { label: 'Venues',         href: '/wedding-venues' },
  { label: 'Vendors',        href: '/wedding-vendors' },
  { label: 'Destinations',   href: '/all-destinations' },
  { label: 'LWD Edit',       href: '/lwd-edit' },
  { label: 'Publications',   href: '/publications' },
  { label: 'Members',        href: '/community' },
];

const linkStyle = {
  fontFamily:     FU,
  fontSize:       8,                  // smaller than the Edit (utility, not voice)
  fontWeight:     400,                // lighter, recedes behind THE EDIT below
  letterSpacing:  '0.34em',           // even wider tracking, quietly authoritative
  textTransform:  'uppercase',
  color:          INK_DIM,
  textDecoration: 'none',
  flexShrink:     0,
  transition:     'color 0.2s',
  whiteSpace:     'nowrap',
};

export default function DirectoryNav({ onNavigateHome } = {}) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const goHome = (e) => {
    e.preventDefault();
    if (onNavigateHome) onNavigateHome();
    else navigateTo('/');
  };

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check, { passive: true });
    return () => window.removeEventListener('resize', check);
  }, []);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <>
    <div style={{
      position:        'sticky',
      top:             0,
      zIndex:          100,
      width:           '100%',
      height:          isMobile ? 52 : DIRECTORY_NAV_HEIGHT,
      background:      IVORY_DIM,
      backdropFilter:  'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderBottom:    `1px solid ${HAIRLINE}`,
    }}>

      {/* ── MOBILE: LWD wordmark + hamburger ─────────────────────────────── */}
      {isMobile ? (
        <nav style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
          <a href="/" onClick={goHome} aria-label="Home" title="Home" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img src={LOGO_BLACK} alt="Luxury Wedding Directory" style={{ height: 18, width: 'auto', display: 'block' }} />
          </a>
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}
          >
            <span style={{ width: 22, height: 1.5, background: INK_FULL, borderRadius: 1, display: 'block' }} />
            <span style={{ width: 15, height: 1.5, background: GOLD,     borderRadius: 1, display: 'block' }} />
            <span style={{ width: 22, height: 1.5, background: INK_FULL, borderRadius: 1, display: 'block' }} />
          </button>
        </nav>
      ) : (

      /* ── DESKTOP: full nav row ─────────────────────────────────────────── */
      <nav style={{ height: '100%', display: 'flex', alignItems: 'center', padding: '0 clamp(24px, 6vw, 80px)' }}>
        <a href="/" onClick={goHome} aria-label="Home" title="Home" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img src={LOGO_BLACK} alt="Luxury Wedding Directory" style={{ height: 20, width: 'auto', display: 'block' }} />
        </a>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'clamp(14px, 1.4vw, 24px)', paddingLeft: 'clamp(40px, 6vw, 90px)', paddingRight: 'clamp(20px, 2.4vw, 36px)', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {ITEMS.map(item => (
            <a key={item.label} href={item.href} style={linkStyle}
              onMouseEnter={e => e.currentTarget.style.color = GOLD}
              onMouseLeave={e => e.currentTarget.style.color = INK_DIM}
            >{item.label}</a>
          ))}
        </div>
        <span aria-hidden style={{ width: 1, height: 16, background: `${GOLD}55`, marginRight: 'clamp(12px, 1.6vw, 24px)', flexShrink: 0 }} />
        <a href="/apply-for-inclusion"
          style={{ fontFamily: FU, fontSize: 8, fontWeight: 500, letterSpacing: '0.30em', textTransform: 'uppercase', color: GOLD, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, flexShrink: 0, transition: 'opacity 0.2s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.65'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          Apply for Inclusion
          <span aria-hidden style={{ width: 4, height: 4, background: GOLD, transform: 'rotate(45deg)', flexShrink: 0 }} />
        </a>
      </nav>
      )}
    </div>

    {/* ── Mobile drawer ─────────────────────────────────────────────────── */}
    {isMobile && createPortal(
      <>
        {/* Backdrop */}
        <div onClick={() => setDrawerOpen(false)} style={{
          position: 'fixed', inset: 0, zIndex: 1200,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
          opacity: drawerOpen ? 1 : 0, pointerEvents: drawerOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }} />
        {/* Panel */}
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1201,
          width: 'clamp(260px, 80vw, 320px)',
          background: IVORY_DIM,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex', flexDirection: 'column',
          paddingBottom: 32,
        }}>
          {/* Drawer header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${HAIRLINE}` }}>
            <span style={{ fontFamily: FU, fontSize: 10, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: INK_FULL }}>Menu</span>
            <button onClick={() => setDrawerOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: INK_DIM, cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
          </div>
          {/* Links */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {ITEMS.map(item => (
              <a key={item.label} href={item.href} style={{
                display: 'block', padding: '14px 24px',
                fontFamily: FU, fontSize: 11, fontWeight: 400,
                letterSpacing: '0.28em', textTransform: 'uppercase',
                color: INK_FULL, textDecoration: 'none',
                borderBottom: `1px solid ${HAIRLINE}`,
                transition: 'color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.color = GOLD}
                onMouseLeave={e => e.currentTarget.style.color = INK_FULL}
              >{item.label}</a>
            ))}
            <a href="/apply-for-inclusion" style={{
              display: 'block', padding: '16px 24px',
              fontFamily: FU, fontSize: 11, fontWeight: 600,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD, textDecoration: 'none',
              transition: 'opacity 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.65'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >Apply for Inclusion ◆</a>
          </div>
        </div>
      </>,
      document.body
    )}
    </>
  );
}

// Single source of truth for the bar's height, consumed by the Masthead so
// the two layers stay in sync without copy-pasted magic numbers.
export const DIRECTORY_NAV_HEIGHT = 34;
// Reference to INK_FULL kept so future "active link" styling has it on hand.
export { INK_FULL };
